import PropTypes from 'prop-types'
import { useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Decimal from 'decimal.js'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import { getBalance } from 'models/ProfileInfo/selectors'
import redemptionsActions from 'models/Redemptions/actions'

import { EVENT_TYPE, STRING } from 'resources'
import { APP_URLS } from 'resources/urls'

import { intercomShowNewMessage } from 'services/Intercom'

import { sendAnalyticsEvent } from 'services/analytics'
import getDecimalValue from 'utils/get-decimal-value'

import SharesAmountSelector from './SharesAmountSelector'

import { RedemptionSelectionContainer, RedemptionSelectionTitle } from './styles'

const recaptchaSharesAction = 'web_shares_redemption'

const RedemptionSelection = ({ reward }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const balance = useSelector(getBalance)

  const maxValue = getDecimalValue(reward.redemptionValues[reward.redemptionValues.length - 1] || 0)
  const maxAmount = Decimal.min(balance, maxValue)
  const [selectedAmount, setSelectedAmount] = useState(maxAmount.div(Decimal(reward.sharePrice)).floor().toString())
  const [errorMessage, setErrorMessage] = useState('')
  const [isRequesting, setIsRequesting] = useState(false)

  // Functions for Shares Redemption
  const confirmSharesCallback = useCallback(
    (isSuccess, response) => {
      setIsRequesting(false)
      if (!isSuccess) {
        if (response?.code === 'RecaptchaVerificationException') {
          sendAnalyticsEvent(EVENT_TYPE.captchaFail, { screenName: 'Redemption', score: response?.score })
          setErrorMessage(STRING.wallet.redemptions.recaptchaErrorMessage)
          intercomShowNewMessage(STRING.wallet.redemptions.recaptchaErrorSupport('redemption'))
        } else {
          setErrorMessage(STRING.wallet.redemptions.shares.redeemFailureMessage)
        }
        return
      }

      // Redirect to the document signing page
      history.push({
        pathname: APP_URLS.SHARES_FOR_REDEMPTION_SIGNING,
        state: {
          documentSlug: response?.documentSlug,
        },
      })
    },
    [history],
  )

  const handleRequestSharesConfirmClick = useCallback(
    (recaptchaToken) => {
      setIsRequesting(true)
      setErrorMessage('')
      sendAnalyticsEvent(EVENT_TYPE.redemptionSharesRequested, { amount: selectedAmount })
      dispatch(redemptionsActions.requestSharesRedemption.init(selectedAmount, recaptchaToken, confirmSharesCallback))
    },
    [dispatch, selectedAmount, confirmSharesCallback],
  )

  const handleRequestSharesRedemption = useCallback(async () => {
    try {
      const recaptchaToken = await executeRecaptcha(recaptchaSharesAction)
      handleRequestSharesConfirmClick(recaptchaToken)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`[Recaptcha] Recaptcha error`, error)
      setErrorMessage(STRING.wallet.redemptions.recaptchaErrorMessage)
      intercomShowNewMessage(STRING.wallet.redemptions.recaptchaErrorSupport('redemption'))
    }
  }, [executeRecaptcha, handleRequestSharesConfirmClick])

  return (
    <RedemptionSelectionContainer>
      <RedemptionSelectionTitle>
        {STRING.wallet.redemptions.giftCardRedemption.redeemSelectionTitle}
      </RedemptionSelectionTitle>
      <SharesAmountSelector
        reward={reward}
        selectedNumberOfShares={selectedAmount}
        setSelectedNumberOfShares={setSelectedAmount}
        onRequest={handleRequestSharesRedemption}
        isRequesting={isRequesting}
        errorMessage={errorMessage}
      />
    </RedemptionSelectionContainer>
  )
}

RedemptionSelection.propTypes = {
  reward: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    categoryId: PropTypes.string.isRequired,
    isTop: PropTypes.bool.isRequired,
    imageUrl: PropTypes.string.isRequired,
    redemptionValues: PropTypes.arrayOf(PropTypes.string).isRequired,
    currentPrice: PropTypes.string,
    dayChange: PropTypes.string,
    fees: PropTypes.string,
    sharePrice: PropTypes.string,
    minShares: PropTypes.number,
  }).isRequired,
}

export default RedemptionSelection
